/**
 * Orden de la página cargada en Ventas → listado e historial de facturas.
 * No toca paginación ni filtros: reordena las filas ya recibidas.
 */
import { normalizeClienteTiposPermitidos } from "@/lib/clienteTipoFactura";

export type FacturaSortKey =
  | "numero"
  | "presupuesto"
  | "cliente"
  | "documento"
  | "fecha"
  | "total"
  | "estado";

export type SortDirection = "asc" | "desc";

export type FacturaSortState = {
  key: FacturaSortKey | null;
  dir: SortDirection;
};

type SortCliente = {
  nombre?: string | null;
  codigo?: string | null;
  cuit?: string | null;
  dni?: string | null;
  tipoFacturaPermitida?: unknown;
};

export type FacturaSortRow = {
  numero?: string | null;
  nroPresupuesto?: string | null;
  presupuestoId?: number | string | null;
  tipoFactura?: string | null;
  tipoComprobante?: string | null;
  fecha?: string | null;
  total?: number | string | null;
  estado?: string | null;
  estadoFiscal?: string | null;
  cliente?: SortCliente | null;
};

export function facturaEstadoLabel(factura: FacturaSortRow): string {
  const isNC = (factura.tipoComprobante || "").toUpperCase().startsWith("NC-");
  const estado = factura.estado?.toLowerCase() || "";
  const fiscal = factura.estadoFiscal;
  let label: string = factura.estado || "";

  if (isNC && fiscal === "AUTORIZADO") label = "NC fiscal autorizada";
  else if (isNC && fiscal === "LOCAL") label = "NC-X local";
  else if (isNC && fiscal === "INCIERTO") label = "NC fiscal incierta";
  else if (isNC && fiscal === "RECHAZADO") label = "NC rechazada";
  else if (isNC) label = factura.estado || "NC pendiente";
  else if (fiscal === "INCIERTO" || fiscal === "ENVIANDO") label = "Emisión incierta";
  else if (fiscal === "RECHAZADO") label = "Rechazada ARCA";
  else if (estado.includes("cae") || estado.includes("afip")) label = "CAE/ARCA";
  else if (estado.includes("negro")) label = "FX";
  else if (estado === "pendiente") label = "Pendiente";

  return label;
}

export function facturaClienteLabel(factura: FacturaSortRow): string {
  const cliente = factura.cliente;
  if (cliente?.codigo === "CF") return "C.Final";
  return cliente?.nombre || "";
}

export function facturaDocumentoLabel(factura: FacturaSortRow): string {
  const cliente = factura.cliente;
  const tiposCliente = normalizeClienteTiposPermitidos(cliente?.tipoFacturaPermitida);
  const showDni =
    factura.tipoFactura === "B" ||
    (!factura.tipoFactura && tiposCliente.includes("B") && !tiposCliente.includes("A"));
  const idValue = showDni ? (cliente?.dni || cliente?.cuit) : cliente?.cuit;
  return idValue || "";
}

export function facturaSortValue(factura: FacturaSortRow, key: FacturaSortKey): string | number {
  switch (key) {
    case "numero":
      return factura.numero || "";
    case "presupuesto":
      return String(factura.nroPresupuesto || factura.presupuestoId || "");
    case "cliente":
      return facturaClienteLabel(factura);
    case "documento":
      return facturaDocumentoLabel(factura);
    case "fecha":
      return factura.fecha || "";
    case "total":
      return Number(factura.total ?? 0);
    case "estado":
      return facturaEstadoLabel(factura);
    default:
      return "";
  }
}

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  }
  return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
}

export function compareFacturaRows(
  a: FacturaSortRow,
  b: FacturaSortRow,
  key: FacturaSortKey,
  dir: SortDirection,
): number {
  const cmp = compareValues(facturaSortValue(a, key), facturaSortValue(b, key));
  if (cmp !== 0) return dir === "asc" ? cmp : -cmp;
  const tie = compareValues(a.numero || "", b.numero || "");
  return dir === "asc" ? tie : -tie;
}

export function sortFacturaRows<T extends FacturaSortRow>(
  rows: T[],
  state: FacturaSortState,
): T[] {
  if (!state.key) return rows;
  const key = state.key;
  return [...rows].sort((a, b) => compareFacturaRows(a, b, key, state.dir));
}

export function toggleFacturaSort(current: FacturaSortState, key: FacturaSortKey): FacturaSortState {
  if (current.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: "asc" };
}
